import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Mail, KeyRound, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PasswordInput from '@/components/PasswordInput';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FieldGroup = ({ children }) => (
  <div className="space-y-2">{children}</div>
);

/**
 * Account & Security card on the Profile page.
 *
 * Two independent forms (email + password), each with their own current-password
 * challenge. Google-OAuth users see a different password panel that links them
 * to google.com/security since their auth lives on Google's side.
 */
const AccountSecurityCard = ({ user, onEmailChanged }) => {
  const [hasPassword, setHasPassword] = useState(true);

  // Email form
  const [newEmail, setNewEmail] = useState('');
  const [emailCurrentPw, setEmailCurrentPw] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  // Password form
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmNewPw, setConfirmNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const refreshHasPassword = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/has-password`, { withCredentials: true });
      setHasPassword(!!res.data?.has_password);
    } catch (err) {
      // Default to showing the form — worst case backend rejects with a clear error.
      setHasPassword(true);
    }
  }, []);

  useEffect(() => {
    refreshHasPassword();
  }, [refreshHasPassword]);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !emailCurrentPw) return;
    if (newEmail.trim().toLowerCase() === (user?.email || '').toLowerCase()) {
      toast.error('New email matches your current one');
      return;
    }
    setEmailSaving(true);
    try {
      const res = await axios.put(
        `${API}/user/email`,
        { current_password: emailCurrentPw, new_email: newEmail.trim() },
        { withCredentials: true }
      );
      toast.success('Email updated');
      setNewEmail('');
      setEmailCurrentPw('');
      onEmailChanged?.(res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || err.message;
      toast.error(`Could not update email: ${msg}`);
    } finally {
      setEmailSaving(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPw !== confirmNewPw) {
      toast.error('New password and confirmation don\'t match');
      return;
    }
    setPwSaving(true);
    try {
      await axios.put(
        `${API}/user/password`,
        { current_password: currentPw, new_password: newPw },
        { withCredentials: true }
      );
      toast.success('Password updated');
      setCurrentPw('');
      setNewPw('');
      setConfirmNewPw('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || err.message;
      toast.error(`Could not update password: ${msg}`);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div
      className="bg-card rounded-2xl border border-border/50 p-6 space-y-6"
      data-testid="account-security-card"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold leading-tight">Account &amp; Security</h2>
          <p className="text-xs text-muted-foreground">Update your email and password.</p>
        </div>
      </div>

      {/* --- Email --- */}
      <form onSubmit={submitEmail} className="space-y-3" data-testid="change-email-form">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Mail className="h-4 w-4 text-muted-foreground" />
          Change email
        </div>
        <p className="text-xs text-muted-foreground">
          Current email: <span className="text-foreground font-medium" data-testid="current-email">{user?.email}</span>
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <FieldGroup>
            <Label htmlFor="new-email">New email</Label>
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@new-address.com"
              data-testid="new-email-input"
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="email-current-pw">Current password</Label>
            <PasswordInput
              id="email-current-pw"
              value={emailCurrentPw}
              onChange={(e) => setEmailCurrentPw(e.target.value)}
              autoComplete="current-password"
              data-testid="email-current-pw-input"
            />
          </FieldGroup>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={emailSaving || !newEmail.trim() || !emailCurrentPw || !hasPassword}
            data-testid="change-email-submit"
          >
            {emailSaving ? 'Saving…' : 'Update email'}
          </Button>
        </div>
        {!hasPassword && (
          <p className="text-xs text-muted-foreground" data-testid="email-oauth-disabled-note">
            Your account is linked to Google — manage your sign-in details on Google instead.
          </p>
        )}
      </form>

      <div className="h-px bg-border/50" />

      {/* --- Password --- */}
      {hasPassword ? (
        <form onSubmit={submitPassword} className="space-y-3" data-testid="change-password-form">
          <div className="flex items-center gap-2 text-sm font-medium">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            Change password
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <FieldGroup>
              <Label htmlFor="pw-current">Current password</Label>
              <PasswordInput
                id="pw-current"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                data-testid="pw-current-input"
              />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="pw-new">New password</Label>
              <PasswordInput
                id="pw-new"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                data-testid="pw-new-input"
              />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <PasswordInput
                id="pw-confirm"
                value={confirmNewPw}
                onChange={(e) => setConfirmNewPw(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                data-testid="pw-confirm-input"
              />
            </FieldGroup>
          </div>
          <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={pwSaving || !currentPw || !newPw || !confirmNewPw}
              data-testid="change-password-submit"
            >
              {pwSaving ? 'Saving…' : 'Update password'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-3" data-testid="oauth-password-panel">
          <div className="flex items-center gap-2 text-sm font-medium">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            Password
          </div>
          <p className="text-sm text-muted-foreground">
            You signed in with Google, so we don&apos;t store a password for you. Manage your
            password and security settings directly on Google.
          </p>
          <Button
            variant="outline"
            asChild
            data-testid="oauth-manage-google-btn"
          >
            <a
              href="https://myaccount.google.com/security"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              Manage at Google
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccountSecurityCard;
